import React, { useState, useEffect } from 'react'; // eslint-disable-line
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { api, downloadCSV } from './shared';

// Campaign config — เป้าหมาย + ระยะเวลา
const CAMPAIGN_START = '2026-04-27';
const CAMPAIGN_END = '2026-05-27';
const TARGETS = {
  visitors: 12000,
  players: 4000,
  bills: 3000,
  teams: 600,
  spend: 1800000,
};
const TH_MONTHS = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.'];
const fmtThaiShort = (iso) => {
  const d = new Date(iso);
  return `${d.getDate()} ${TH_MONTHS[d.getMonth()]} ${(d.getFullYear()+543)%100}`;
};
const todayStr = () => new Date().toISOString().split('T')[0];
const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);
const pct = (n, target) => target > 0 ? Math.min(100, (n / target) * 100) : 0;
const pctLabel = (n, target) => `${pct(n, target).toFixed(1)}%`;

const DailyTab = ({ onError }) => {
  const [daily, setDaily] = useState(null);
  const [totals, setTotals] = useState({ totalUniquePlayers: 0, totalPeopleEntered: 0 });
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const hasDateFilter = !!(startDate || endDate);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await api('daily-summary');
      setDaily(r.data.data);
      setTotals({
        totalUniquePlayers: r.data.totalUniquePlayers || 0,
        totalPeopleEntered: r.data.totalPeopleEntered || 0,
      });
    } catch(e) { onError(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []); // eslint-disable-line

  // Filter ตาม startDate–endDate (ถ้าเลือก) — กระทบ cards + chart + table
  const filteredDaily = daily ? (() => {
    if (!startDate && !endDate) return daily;
    const s = startDate || endDate;
    const e = endDate || startDate;
    return daily.filter(d => d.date >= s && d.date <= e);
  })() : null;
  const isSingleDay = filteredDaily && hasDateFilter && filteredDaily.length === 1;

  // เตรียม data กราฟ (เรียงวันเก่า → ใหม่)
  const chartData = filteredDaily ? [...filteredDaily].reverse().map(d => ({
    date: d.date.substring(5),
    fullDate: d.date,
    'ผู้เล่นสแกน': d.playersTotal || 0,
    'บิลรวม': d.billTotal,
  })) : [];


  return (
    <div className="bts-admin__section">
      <div className="bts-admin__topbar">
        <div className="bts-admin__page-title">📊 สรุปรายวัน</div>
        <div className="bts-admin__actions">
          <button className="bts-admin__btn bts-admin__btn--dark" onClick={fetch} disabled={loading}>{loading ? 'โหลด...' : 'รีเฟรช'}</button>
          {daily && <button className="bts-admin__btn bts-admin__btn--green bts-admin__btn--sm" onClick={() => downloadCSV(
            ['วันที่','คนเข้า (วันนั้น)','ผู้เล่น (วันนั้น)','New Member','รอสร้างทีม','ทีมใหม่','ครบ 4 คน','บิลสำเร็จ','บิลรอคิว','บิลรวม','ยอดสำเร็จ','ยอดรอคิว','ยอดรวม'],
            daily.map(d => [d.date,d.peopleEntered||0,d.playersFirstTime||0,d.newMembers||0,d.waitingPlayers,d.teamsNew,d.teamsComplete,d.receipts,d.pendings,d.billTotal,d.receiptSpend,d.pendingSpend,d.totalSpend]),
            'bts_daily.csv'
          )}>Export CSV</button>}
        </div>
      </div>

      {/* Date filter — มุมซ้ายใต้ header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14, padding:'14px 18px', background:'#f8f9fa', borderRadius:10, border:'1px solid #e0e0e0', flexWrap:'wrap' }}>
        <span style={{ fontSize:16, fontWeight:700, color:'#333' }}>📅 ช่วงวันที่:</span>
        <input type="date" value={startDate} min={CAMPAIGN_START} max={endDate || todayStr()}
          onChange={e => setStartDate(e.target.value)}
          style={{
            padding:'10px 14px', borderRadius:8,
            border: hasDateFilter ? '2px solid #459efc' : '1px solid #ccc',
            fontSize:16, fontWeight:600, fontFamily:'inherit',
            background:'#fff', cursor:'pointer', minWidth:170,
          }} />
        <span style={{ fontSize:14, color:'#666' }}>ถึง</span>
        <input type="date" value={endDate} min={startDate || CAMPAIGN_START} max={todayStr()}
          onChange={e => setEndDate(e.target.value)}
          style={{
            padding:'10px 14px', borderRadius:8,
            border: hasDateFilter ? '2px solid #459efc' : '1px solid #ccc',
            fontSize:16, fontWeight:600, fontFamily:'inherit',
            background:'#fff', cursor:'pointer', minWidth:170,
          }} />
        {hasDateFilter && (
          <button onClick={() => { setStartDate(''); setEndDate(''); }}
            style={{ background:'#E24B4A', color:'#fff', border:'none', borderRadius:8, padding:'9px 18px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            ล้างตัวกรอง
          </button>
        )}
        {!hasDateFilter && (
          <span style={{ fontSize:13, color:'#888' }}>
            (ไม่เลือก = แสดงทุกวัน) เลือกได้ {fmtThaiShort(CAMPAIGN_START)} – วันนี้ — เลือกวันเดียวก็ได้ (กรอกเฉพาะช่อง "ตั้งแต่")
          </span>
        )}
      </div>
      {loading && <p>กำลังโหลด...</p>}
      {daily && (() => {
        const src = filteredDaily;
        const visitors = src.reduce((s,d)=>s+(d.peopleEntered||0),0);
        const players = isSingleDay
          ? (src[0]?.playersTotal || 0)              // วันเดียว → ใช้ active per day
          : (hasDateFilter
              ? src.reduce((s,d)=>s+(d.playersTotal||0),0) // ช่วงวัน → SUM per-day (visit-days)
              : totals.totalUniquePlayers);                 // ทั้ง campaign → ใช้ unique total
        const teams = src.reduce((s,d)=>s+(d.teamsNew||0),0);
        const spend = src.reduce((s,d)=>s+(d.totalSpend||0),0);
        const totalDays = daysBetween(CAMPAIGN_START, CAMPAIGN_END);
        const today = todayStr();
        const elapsedRaw = daysBetween(CAMPAIGN_START, today);
        const daysElapsed = Math.max(0, Math.min(totalDays, elapsedRaw));
        const daysLeft = Math.max(0, totalDays - daysElapsed);
        return (<>
        <div style={{ background:'#fff8e6', border:'1px solid #FF8A00', borderRadius:8, padding:12, marginBottom:16, fontSize:13, color:'#7a4d00', textAlign:'left' }}>
          <div>📅 <b>ระยะเวลาแคมเปญ:</b> {fmtThaiShort(CAMPAIGN_START)} – {fmtThaiShort(CAMPAIGN_END)} (รวม {totalDays} วัน)</div>
          <div style={{ marginTop:4 }}>⏱ <b>ดำเนินการมา {daysElapsed} วัน</b> • เหลืออีก <b style={{ color:'#E24B4A' }}>{daysLeft} วัน</b></div>
        </div>
        <div className="bts-admin__cards" style={{ gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <div className="bts-admin__card" style={{ borderColor:'#FF8A00', textAlign:'center' }}>
            <div style={{ fontWeight:700, fontSize:15, color:'#333' }}>จำนวนคนเข้าทั้งหมด</div>
            <div style={{ fontSize:15, color:'#888', marginTop:2 }}>Target {TARGETS.visitors.toLocaleString()} คน</div>
            <div style={{ fontSize:13, color:'#333', marginTop:2 }}>ได้ <b style={{ color:'#FF8A00', fontSize:22 }}>{visitors.toLocaleString()}</b> คน</div>
            <div style={{ fontSize:15, color:'#888', marginTop:2 }}>= <b style={{ color:'#FF8A00' }}>{pctLabel(visitors, TARGETS.visitors)}</b></div>
          </div>
          <div className="bts-admin__card" style={{ borderColor:'#9B59B6', textAlign:'center' }}>
            <div style={{ fontWeight:700, fontSize:15, color:'#333' }}>จำนวนผู้เล่นสแกนทั้งหมด</div>
            <div style={{ fontSize:15, color:'#888', marginTop:2 }}>Target {TARGETS.players.toLocaleString()} คน</div>
            <div style={{ fontSize:13, color:'#333', marginTop:2 }}>ได้ <b style={{ color:'#9B59B6', fontSize:22 }}>{players.toLocaleString()}</b> คน</div>
            <div style={{ fontSize:15, color:'#888', marginTop:2 }}>= <b style={{ color:'#9B59B6' }}>{pctLabel(players, TARGETS.players)}</b></div>
          </div>
          <div className="bts-admin__card" style={{ borderColor:'#10C851', textAlign:'center' }}>
            <div style={{ fontWeight:700, fontSize:15, color:'#333' }}>จำนวนทีมร่วมทั้งหมด</div>
            <div style={{ fontSize:15, color:'#888', marginTop:2 }}>Target {TARGETS.teams.toLocaleString()} ทีม</div>
            <div style={{ fontSize:13, color:'#333', marginTop:2 }}>ได้ <b style={{ color:'#10C851', fontSize:22 }}>{teams.toLocaleString()}</b> ทีม</div>
            <div style={{ fontSize:15, color:'#888', marginTop:2 }}>= <b style={{ color:'#10C851' }}>{pctLabel(teams, TARGETS.teams)}</b></div>
          </div>
          <div className="bts-admin__card" style={{ borderColor:'#459efc', textAlign:'center' }}>
            <div style={{ fontWeight:700, fontSize:15, color:'#333' }}>ยอดซื้อรวม</div>
            <div style={{ fontSize:15, color:'#888', marginTop:2 }}>Target {TARGETS.spend.toLocaleString()} บาท</div>
            <div style={{ fontSize:13, color:'#333', marginTop:2 }}>ได้ <b style={{ color:'#459efc', fontSize:22 }}>฿{spend.toLocaleString()}</b></div>
            <div style={{ fontSize:15, color:'#888', marginTop:2 }}>= <b style={{ color:'#459efc' }}>{pctLabel(spend, TARGETS.spend)}</b></div>
          </div>
          <div className="bts-admin__card" style={{ borderColor:'#333', textAlign:'center' }}>
            <div style={{ fontWeight:700, fontSize:15, color:'#333' }}>จำนวนบิลรวม</div>
            <div style={{ fontSize:15, color:'#888', marginTop:2 }}>Target {TARGETS.bills.toLocaleString()} บิล</div>
            <div style={{ fontSize:13, color:'#333', marginTop:2 }}>ได้ <b style={{ color:'#333', fontSize:22 }}>{src.reduce((s,d)=>s+(d.billTotal||0),0).toLocaleString()}</b> บิล</div>
            <div style={{ fontSize:15, color:'#888', marginTop:2 }}>= <b style={{ color:'#333' }}>{pctLabel(src.reduce((s,d)=>s+(d.billTotal||0),0), TARGETS.bills)}</b></div>
          </div>
        </div>

        {/* กราฟผู้เล่นสแกนรายวัน + เลือกวันดูได้ */}
        <div style={{ background:'#fff', borderRadius:12, padding:'16px 8px', marginBottom:16, border:'1px solid #eee' }}>
          <div style={{ paddingLeft:8, marginBottom:8 }}>
            <p style={{ fontWeight:700, fontSize:14 }}>ผู้เล่นสแกนรายวัน</p>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background:'#fff', border:'1px solid #ddd', borderRadius:8, padding:'8px 12px', fontSize:12 }}>
                    <p style={{ fontWeight:700, marginBottom:4 }}>{d.fullDate}</p>
                    <p style={{ color:'#9B59B6' }}>ผู้เล่นสแกน: <b>{d['ผู้เล่นสแกน']}</b></p>
                    <p style={{ color:'#333' }}>บิลรวม: <b>{d['บิลรวม']}</b></p>
                  </div>
                );
              }} />
              <Legend />
              <Line type="monotone" dataKey="ผู้เล่นสแกน" stroke="#9B59B6" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
              <Line type="monotone" dataKey="บิลรวม" stroke="#333" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" />

            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bts-admin__table-wrap">
          <table className="bts-admin__table">
            <thead><tr><th>วันที่</th><th>คนเข้า (วันนั้น)</th><th>ผู้เล่น (วันนั้น)</th><th>New Member</th><th>รอสร้างทีม</th><th>ทีมใหม่</th><th>ครบ 4 คน</th><th>บิลสำเร็จ</th><th>บิลรอคิว</th><th>บิลรวม</th><th>ยอดสำเร็จ</th><th>ยอดรอคิว</th><th>ยอดรวม</th></tr></thead>
            <tbody>
              {src.map((d, i) => (
                <tr key={i}>
                  <td><b>{d.date}</b></td><td><b>{d.peopleEntered||0}</b></td><td><b>{d.playersFirstTime||0}</b></td><td>{d.newMembers||0}</td><td>{d.waitingPlayers}</td><td>{d.teamsNew}</td><td>{d.teamsComplete}</td><td>{d.receipts}</td><td>{d.pendings}</td><td><b>{d.billTotal}</b></td><td>฿{d.receiptSpend.toLocaleString()}</td><td>฿{d.pendingSpend.toLocaleString()}</td><td><b>฿{d.totalSpend.toLocaleString()}</b></td>
                </tr>
              ))}
              {!isSingleDay && src.length > 1 && (
                <tr className="bts-admin__row--total">
                  <td><b>รวม</b></td>
                  <td><b>{src.reduce((s,d)=>s+(d.peopleEntered||0),0)}</b></td>
                  <td><b>{src.reduce((s,d)=>s+(d.playersFirstTime||0),0)}</b></td>
                  <td><b>{src.reduce((s,d)=>s+(d.newMembers||0),0)}</b></td>
                  <td>{src.reduce((s,d)=>s+d.waitingPlayers,0)}</td><td>{src.reduce((s,d)=>s+d.teamsNew,0)}</td><td>{src.reduce((s,d)=>s+d.teamsComplete,0)}</td>
                  <td>{src.reduce((s,d)=>s+d.receipts,0)}</td><td>{src.reduce((s,d)=>s+d.pendings,0)}</td><td><b>{src.reduce((s,d)=>s+d.billTotal,0)}</b></td>
                  <td>฿{src.reduce((s,d)=>s+d.receiptSpend,0).toLocaleString()}</td><td>฿{src.reduce((s,d)=>s+d.pendingSpend,0).toLocaleString()}</td><td><b>฿{src.reduce((s,d)=>s+d.totalSpend,0).toLocaleString()}</b></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </>);
      })()}
    </div>
  );
};

export default DailyTab;
